import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// ** MUI Imports
import Drawer from "@mui/material/Drawer";
import { useTheme } from "@mui/material/styles";

// ** Icons
import Close from "mdi-material-ui/Close";
import PlayCircleOutline from "mdi-material-ui/PlayCircleOutline";
import CheckCircle from "mdi-material-ui/CheckCircle";
import ArrowRight from "mdi-material-ui/ArrowRight";

// ** Utils
import moment from "moment-timezone";
import { hasAccess, accessEnded } from "src/@core/utils/course-date";
import { courseProgress } from "src/@core/utils/course-progress";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";
import "src/styles/style-v3.css";

const isMobile = window.innerWidth <= 760;
const defaultThumbnail = "/images/default-course.png";

/******************************************************************
 * ##: CourseDrawer
 * Right-side course detail drawer (style-v3), opened from a course
 * thumbnail. Shows the cover, title, lesson count, progress, the
 * description and a pinned resume/start CTA, plus an access-ending
 * warning when the course access expires soon.
 * @param {Object} data - the course record
 * @param {Boolean} visible - drawer open state
 * @param {Function} close - close handler
 * @returns {JSX.Element} - the MUI Drawer wrapping v3 content
 * History:
 * 05-07-2026: Rebuilt with style-v3 (single pinned CTA, cover hero)
 ******************************************************************/
export default function CourseDrawer({ data, visible, close }) {
  const location = useLocation();
  const theme = useTheme();
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (data?.courses_students?.ended_at) {
      const diffInDays = moment(data.courses_students.ended_at).diff(moment(), "days");
      if (diffInDays < 15) setEndDate(data.courses_students.ended_at);
      else setEndDate(null);
    } else {
      setEndDate(null);
    }
  }, [data]);

  const progress = courseProgress(data);
  const done = progress >= 100;
  const ended = accessEnded(data?.courses_students?.ended_at);
  const lessons = data?.courses_students?.lessons_count || data?.active_lessons_count || 0;
  const courseHref = data?.course_id ? pathWithPreservedSearch(`/courses/${data.course_id}`, location.search) : "#";

  const ctaLabel = () => {
    if (ended) return "Acesso terminado";
    if (done) return "Rever curso";
    if (data?.courses_students?.started === true) return "Continuar a ver";
    return "Iniciar curso";
  };

  const DrawerContent = () => (
    // The drawer is portaled outside the app's `.page-theme-dark` wrapper,
    // so re-declare it here (from the MUI theme) to keep dark mode working.
    <div className={theme.palette.mode === "dark" ? "page-theme-dark" : undefined} style={{ height: "100%" }}>
      <div className="spv3 spv3-drawer">
        <button type="button" className="spv3-drawer__close" aria-label="Fechar" onClick={close}>
          <Close />
        </button>

        <div className="spv3-drawer__media">
          {done && (
            <span className="spv3-drawer__badge spv3-pill spv3-pill--success">
              <CheckCircle />
              Concluído
            </span>
          )}
          <img className="spv3-drawer__img" alt={data?.title} src={data?.image || defaultThumbnail} />
        </div>

        <div className="spv3-drawer__body">
          {endDate && (
            <div className="spv3-note spv3-note--warning">
              {hasAccess(endDate) === false
                ? "O acesso a este curso terminou."
                : `O acesso a este curso termina ${moment.utc(endDate).local().fromNow()}.`}
            </div>
          )}

          <h2 className="spv3-drawer__title">{data?.title}</h2>

          <div className="spv3-drawer__meta">
            <span className="spv3-chip">
              <PlayCircleOutline />
              {lessons === 1 ? "1 aula" : `${lessons} aulas`}
            </span>
          </div>

          <div className={`spv3-drawer__progress${done ? " spv3-course--done" : ""}`}>
            <div className="spv3-course__progress-top">
              <span className="spv3-course__progress-label">{done ? "Curso concluído" : "O teu progresso"}</span>
              <span className="spv3-course__pct">{progress}%</span>
            </div>
            <div className="spv3-course__track">
              <div className="spv3-course__fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {data?.description && (
            <div className="spv3-drawer__section">
              <h3 className="spv3-drawer__section-title">Descrição</h3>
              <div className="spv3-drawer__desc" dangerouslySetInnerHTML={{ __html: data.description }} />
            </div>
          )}
        </div>

        <div className="spv3-drawer__foot">
          {ended ? (
            <span className="spv3-btn spv3-btn--block is-disabled">{ctaLabel()}</span>
          ) : (
            <a className="spv3-btn spv3-btn--primary spv3-btn--block" href={courseHref}>
              {ctaLabel()}
              <ArrowRight className="spv3-btn__arrow" />
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Drawer
      PaperProps={{ sx: { width: isMobile ? "100%" : 480, border: 0 } }}
      anchor="right"
      open={visible}
      onClose={close}
    >
      <DrawerContent />
    </Drawer>
  );
}
